import { DateTime } from 'luxon';
import { db } from '../model/db.js';

export const COMMENT_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export const COMMENT_CACHE_PRUNE_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const COMMENT_CACHE_PRUNE_KEY = 'comment-cache-prune';

/**
 * `CommentSyncMiddleware.ts` only tracks which itemIds are *actively*
 * syncing for the current session (`activeItemIds`, in-memory) - it never
 * touches dexie when an item stops being active, whether because the user
 * navigated away or just closed the tab without logging out. Left alone,
 * `db.comments` would accumulate every item a user has ever viewed.
 *
 * This sweeps that up instead: for each itemId with local comment rows
 * whose sync cursor hasn't advanced in `COMMENT_CACHE_MAX_AGE_MS`, drop its
 * comments and cursor, provided nothing about it is still unpushed. It's
 * itemId-driven (from `db.comments` itself), not `syncMeta`-driven, so it
 * can't touch a *different* collection's cursor (e.g. settings') that
 * happens to share the same table.
 */
export const pruneStaleComments = async (): Promise<void> => {
  // UTC, like the cursors it's compared to (see nextSyncCursor)
  const cutoff = DateTime.utc().minus({ milliseconds: COMMENT_CACHE_MAX_AGE_MS }).toISO();
  const itemIds = (await db.comments.orderBy('itemId').uniqueKeys()) as string[];

  for (const itemId of itemIds) {
    const cursor = await db.syncMeta.get(itemId);
    if (!cursor || cursor.lastSyncedAt >= cutoff) continue;

    const pendingCount = await db.comments
      .where('itemId')
      .equals(itemId)
      .filter((c) => c.syncStatus !== undefined && c.syncStatus !== 'synced')
      .count();
    if (pendingCount > 0) continue;

    await db.transaction('rw', db.comments, db.syncMeta, async () => {
      await db.comments.where('itemId').equals(itemId).delete();
      await db.syncMeta.delete(itemId);
    });
  }
};
